import React from 'react';
import {get, put} from '../../utils/request';
import {Col, Form, OverlayTrigger, Row, Spinner, Tooltip} from 'react-bootstrap';
import Alert from "../../component/Alert/Alert";
import Button from "../../component/Button/Button";
import Container from "../../component/Container/Container";
import Layout from "../../component/Layout/Layout";
import Link from "../../component/Link/Link";
import RoleNav from "../../component/RoleNav/RoleNav";
import {faArrowCircleLeft, faInfoCircle, faSave} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import "../../component/Table/Table.scss"
import './access.scss';

class RoleSettings extends React.Component {
    constructor(props) {
        super(props);
        const roleId = props.match.params.roleId;
        const isEditMode = roleId && roleId !== 'create';

        this.state = {
            roleId: isEditMode ? roleId : null,
            isEditMode: isEditMode,
            data: {
                name: '',
                status: 'active',
                bucketAdmin: false,
                bucketCreate: false,
                bucketDelete: false,
                bucketAccessAll: true
            },
            buckets: [],
            bucketPermissionMap: {},
            errorMessage: "",
            successMessage: "",
            loading: true,
            loadingSuccess: false
        };

        this.loadData = this.loadData.bind(this);
        this.handleNameChange = this.handleNameChange.bind(this);
        this.handleStatusChange = this.handleStatusChange.bind(this);
        this.handleBucketAdminChange = this.handleBucketAdminChange.bind(this);
        this.handleBucketCreateChange = this.handleBucketCreateChange.bind(this);
        this.handleBucketDeleteChange = this.handleBucketDeleteChange.bind(this);
        this.handleBucketAccessAllChange = this.handleBucketAccessAllChange.bind(this);
        this.handleBucketModeChange = this.handleBucketModeChange.bind(this);
        this.handleBucketPermissionChange = this.handleBucketPermissionChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    async componentDidMount() {
        await this.loadData();
    }

    async loadData() {
        const [bucketsRes, bucketsErr] = await get(this, "/auth/bucket/list");
        if (bucketsErr) {
            this.setState({errorMessage: "error loading buckets: " + bucketsErr, loading: false});
            return;
        }
        const buckets = bucketsRes.data.Buckets || [];
        this.setState({buckets: buckets});

        const bucketPermissionMap = {};
        buckets.forEach(b => {
            bucketPermissionMap[b.name + '|' + b.locationUid] = { mode: 'none', permissions: [] };
        });

        if (this.state.isEditMode) {
            const [roleRes, roleErr] = await get(this, "/auth/role/" + this.state.roleId);
            if (roleErr) {
                this.setState({errorMessage: "error loading role: " + roleErr, loading: false});
                return;
            }

            const role = roleRes.data;

            if (role.BucketPermissions && role.BucketPermissions.length > 0) {
                role.BucketPermissions.forEach(bp => {
                    const key = bp.BucketName + '|' + bp.LocationUid;
                    const perms = [];
                    if (bp.List) perms.push('list');
                    if (bp.Read) perms.push('read');
                    if (bp.Write) perms.push('write');
                    if (bp.Delete) perms.push('delete');
                    const hasAll = perms.length === 4;
                    bucketPermissionMap[key] = {
                        mode: hasAll ? 'full' : 'limited',
                        permissions: perms
                    };
                });
            }

            this.setState({
                data: {
                    name: role.Name,
                    status: role.Status,
                    bucketAdmin: role.BucketAdmin,
                    bucketCreate: role.BucketCreate,
                    bucketDelete: role.BucketDelete,
                    bucketAccessAll: role.BucketAccessAll
                },
                bucketPermissionMap: bucketPermissionMap
            });
        } else {
            this.setState({bucketPermissionMap: bucketPermissionMap});
        }

        this.setState({loading: false, loadingSuccess: true});
    }

    handleNameChange(event) {
        const data = {...this.state.data};
        data.name = event.target.value;
        this.setState({data: data});
    }

    handleStatusChange(event) {
        const data = {...this.state.data};
        data.status = event.target.value;
        this.setState({data: data});
    }

    handleBucketAdminChange(event) {
        const data = {...this.state.data};
        data.bucketAdmin = event.target.checked;
        this.setState({data: data});
    }

    handleBucketCreateChange(event) {
        const data = {...this.state.data};
        data.bucketCreate = event.target.checked;
        this.setState({data: data});
    }

    handleBucketDeleteChange(event) {
        const data = {...this.state.data};
        data.bucketDelete = event.target.checked;
        this.setState({data: data});
    }

    handleBucketAccessAllChange(event) {
        const data = {...this.state.data};
        data.bucketAccessAll = event.target.value === 'true';
        this.setState({data: data});
    }

    handleBucketModeChange(bucketKey, mode) {
        const bucketPermissionMap = {...this.state.bucketPermissionMap};
        const current = bucketPermissionMap[bucketKey] || { mode: 'none', permissions: [] };
        bucketPermissionMap[bucketKey] = { ...current, mode: mode };
        this.setState({bucketPermissionMap: bucketPermissionMap});
    }

    handleBucketPermissionChange(bucketKey, permission) {
        const bucketPermissionMap = {...this.state.bucketPermissionMap};
        const current = bucketPermissionMap[bucketKey] || { mode: 'limited', permissions: [] };
        const currentPermissions = [...current.permissions];

        if (currentPermissions.includes(permission)) {
            bucketPermissionMap[bucketKey] = {
                ...current,
                permissions: currentPermissions.filter(p => p !== permission)
            };
        } else {
            bucketPermissionMap[bucketKey] = {
                ...current,
                permissions: [...currentPermissions, permission]
            };
        }
        this.setState({bucketPermissionMap: bucketPermissionMap});
    }

    translateErrorMessage(message) {
        const translations = {
            'either BucketAccessAll must be true or BucketPermissions must not be empty':
                'When using Custom bucket access, you must select at least one bucket with permissions',
            'BucketCreate can only be true if BucketAccessAll is true':
                '"Create buckets" can only be enabled when Bucket access is set to "All"',
            'invalid bucket permission':
                'Invalid bucket permission selected',
            'full permission cannot be combined with other permissions':
                '"Full" permission cannot be combined with other permissions',
            'at least one permission must be set for bucket':
                'Each bucket must have at least one permission selected'
        };
        return translations[message] || message;
    }

    async handleSubmit(event) {
        event.preventDefault();
        this.setState({errorMessage: '', successMessage: ''});

        if (!this.state.data.name.trim()) {
            this.setState({errorMessage: 'Role name is required'});
            window.scrollTo(0, 0);
            return;
        }

        let bucketPermissions = [];
        if (!this.state.data.bucketAccessAll) {
            Object.entries(this.state.bucketPermissionMap).forEach(([key, bucketState]) => {
                const mode = bucketState.mode;
                if (mode === 'full') {
                    const [bucketName, locationUid] = key.split('|');
                    bucketPermissions.push({
                        BucketName: bucketName,
                        LocationUid: locationUid,
                        List: true,
                        Read: true,
                        Write: true,
                        Delete: true
                    });
                } else if (mode === 'limited' && bucketState.permissions.length > 0) {
                    const [bucketName, locationUid] = key.split('|');
                    bucketPermissions.push({
                        BucketName: bucketName,
                        LocationUid: locationUid,
                        List: bucketState.permissions.includes('list'),
                        Read: bucketState.permissions.includes('read'),
                        Write: bucketState.permissions.includes('write'),
                        Delete: bucketState.permissions.includes('delete')
                    });
                }
            });
        }

        const requestData = {
            Name: this.state.data.name,
            Status: this.state.data.status,
            BucketAccessAll: this.state.data.bucketAccessAll,
            BucketAdmin: this.state.data.bucketAdmin,
            BucketCreate: this.state.data.bucketCreate,
            BucketDelete: this.state.data.bucketDelete,
            BucketPermissions: bucketPermissions
        };

        let res, err;
        if (this.state.isEditMode) {
            [res, err] = await put(this, "/auth/role/" + this.state.roleId, requestData);
        } else {
            [res, err] = await put(this, "/auth/role", requestData);
        }

        if (err) {
            let errorMsg = "Error saving role";
            if (err.response && err.response.data && err.response.data.Message) {
                errorMsg = this.translateErrorMessage(err.response.data.Message);
            } else if (err.message) {
                errorMsg = err.message;
            }
            this.setState({errorMessage: errorMsg});
            window.scrollTo(0, 0);
            return;
        }

        if (!this.state.isEditMode) {
            this.setState({
                isEditMode: true,
                roleId: res.data.Id,
                successMessage: 'Role created successfully'
            });
            this.props.history.replace('/access/role/' + res.data.Id);
        } else {
            this.setState({successMessage: 'Role saved successfully'});
            await this.loadData();
        }
        window.scrollTo(0, 0);
    }

    render() {
        const { isEditMode, loading, loadingSuccess, errorMessage, successMessage, data, buckets, bucketPermissionMap, roleId } = this.state;
        return (
            <Layout activeMenu="/access" isLoggedIn={true}>
                <h1>{isEditMode ? <>Role <span className="entity-name">{data.name}</span></> : 'Create new role'}</h1>

                {loading && (
                    <>
                        <Spinner animation="border" variant="primary"/> Loading...
                        <br/><br/>
                    </>
                )}

                {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
                {successMessage && <Alert variant="success">{successMessage}</Alert>}

                {loadingSuccess && (
                    <>
                        {isEditMode && <RoleNav roleId={roleId}/>}
                        <Container>
                            <Form onSubmit={this.handleSubmit}>
                                <h2>General</h2>
                                <Form.Group as={Row} controlId="formRoleName" className="form-fieldnames">
                                    <Form.Label column sm="auto" style={{whiteSpace: 'nowrap', minWidth: '120px'}}>Name</Form.Label>
                                    <Col>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={data.name}
                                            onChange={this.handleNameChange}
                                            placeholder="Enter role name"
                                        />
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} controlId="formStatus" className="form-fieldnames">
                                    <Form.Label column sm="auto" style={{whiteSpace: 'nowrap', minWidth: '120px'}}>Status</Form.Label>
                                    <Col>
                                        <Form.Check
                                            inline
                                            type="radio"
                                            id="status-active"
                                            name="status"
                                            label="Active"
                                            value="active"
                                            checked={data.status === 'active'}
                                            onChange={this.handleStatusChange}
                                        />
                                        <Form.Check
                                            inline
                                            type="radio"
                                            id="status-inactive"
                                            name="status"
                                            label="Inactive"
                                            value="inactive"
                                            checked={data.status === 'inactive'}
                                            onChange={this.handleStatusChange}
                                        />
                                    </Col>
                                </Form.Group>

                                <h2>Role level permissions</h2>
                                <Form.Group as={Row} controlId="formBucketAdmin" className="form-fieldnames">
                                    <Form.Label column sm="auto" style={{whiteSpace: 'nowrap', minWidth: '120px'}}>Admin buckets</Form.Label>
                                    <Col className="d-flex align-items-start">
                                        <Form.Check
                                            type="checkbox"
                                            checked={data.bucketAdmin}
                                            onChange={this.handleBucketAdminChange}
                                        />
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={<Tooltip>
                                                Can modify bucket settings (versioning, object lock, etc.)
                                                <br /><br />NB! Applies only to buckets with Write access!
                                        </Tooltip>}
                                        >
                                            <span style={{marginLeft: '8px', cursor: 'pointer', color: '#6c757d'}}>
                                                <FontAwesomeIcon icon={faInfoCircle}/>
                                            </span>
                                        </OverlayTrigger>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} controlId="formBucketCreate" className="form-fieldnames">
                                    <Form.Label column sm="auto" style={{whiteSpace: 'nowrap', minWidth: '120px'}}>Create buckets</Form.Label>
                                    <Col className="d-flex align-items-start">
                                        <Form.Check
                                            type="checkbox"
                                            checked={data.bucketCreate}
                                            onChange={this.handleBucketCreateChange}
                                        />
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={<Tooltip>Requires Bucket access: All</Tooltip>}
                                        >
                                            <span style={{marginLeft: '8px', cursor: 'pointer', color: '#6c757d'}}>
                                                <FontAwesomeIcon icon={faInfoCircle}/>
                                            </span>
                                        </OverlayTrigger>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} controlId="formBucketDelete" className="form-fieldnames">
                                    <Form.Label column sm="auto" style={{whiteSpace: 'nowrap', minWidth: '120px'}}>Delete buckets</Form.Label>
                                    <Col className="d-flex align-items-start">
                                        <Form.Check
                                            type="checkbox"
                                            checked={data.bucketDelete}
                                            onChange={this.handleBucketDeleteChange}
                                        />
                                        <OverlayTrigger
                                            placement="right"
                                            overlay={<Tooltip>
                                                If "Bucket access: Custom" is selected then requires "Delete" permission for specific bucket to be able to delete it
                                                <br /><br />NB! Applies only to buckets with Delete access!
                                        </Tooltip>}
                                        >
                                            <span style={{marginLeft: '8px', cursor: 'pointer', color: '#6c757d'}}>
                                                <FontAwesomeIcon icon={faInfoCircle}/>
                                            </span>
                                        </OverlayTrigger>
                                    </Col>
                                </Form.Group>

                                <h2>Bucket level permissions</h2>
                                <Form.Group as={Row} controlId="formBucketAccess" className="form-fieldnames">
                                    <Form.Label column sm="auto" style={{whiteSpace: 'nowrap', minWidth: '120px'}}>Bucket access</Form.Label>
                                    <Col>
                                        <Form.Check
                                            type="radio"
                                            id="bucketAccess-all"
                                            name="bucketAccessAll"
                                            label="All"
                                            value="true"
                                            checked={data.bucketAccessAll === true}
                                            onChange={this.handleBucketAccessAllChange}
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="bucketAccess-limited"
                                            name="bucketAccessAll"
                                            label="Custom"
                                            value="false"
                                            checked={data.bucketAccessAll === false}
                                            onChange={this.handleBucketAccessAllChange}
                                        />
                                    </Col>
                                </Form.Group>

                                {data.bucketAccessAll === false && buckets.length > 0 && (
                                    <table className="table-ctr">
                                        <thead>
                                            <tr>
                                                <th>Bucket Name</th>
                                                <th>Location</th>
                                                <th>Permission</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {buckets.map((bucket) => {
                                                const bucketKey = bucket.name + '|' + bucket.locationUid;
                                                const bucketState = bucketPermissionMap[bucketKey] || { mode: 'none', permissions: [] };
                                                const mode = bucketState.mode;
                                                const permissions = bucketState.permissions;
                                                return (
                                                    <tr key={bucketKey}>
                                                        <td data-label="Bucket">{bucket.name}</td>
                                                        <td data-label="Location">{bucket.locationUid}</td>
                                                        <td data-label="Permission" style={{minWidth: '450px'}}>
                                                            <div className="d-flex align-items-center flex-wrap">
                                                                <Form.Check
                                                                    inline
                                                                    type="radio"
                                                                    id={`mode-none-${bucketKey}`}
                                                                    name={`mode-${bucketKey}`}
                                                                    label="None"
                                                                    checked={mode === 'none'}
                                                                    onChange={() => this.handleBucketModeChange(bucketKey, 'none')}
                                                                />
                                                                <Form.Check
                                                                    inline
                                                                    type="radio"
                                                                    id={`mode-full-${bucketKey}`}
                                                                    name={`mode-${bucketKey}`}
                                                                    label="Full"
                                                                    checked={mode === 'full'}
                                                                    onChange={() => this.handleBucketModeChange(bucketKey, 'full')}
                                                                />
                                                                <span style={{
                                                                    border: mode === 'limited' ? (permissions.length === 0 ? '1px solid #dc3545' : '1px solid #dee2e6') : '1px solid transparent',
                                                                    borderRadius: '4px',
                                                                    padding: '4px 8px',
                                                                    backgroundColor: mode === 'limited' ? (permissions.length === 0 ? '#fff5f5' : '#f8f9fa') : 'transparent',
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <Form.Check
                                                                        inline
                                                                        type="radio"
                                                                        id={`mode-limited-${bucketKey}`}
                                                                        name={`mode-${bucketKey}`}
                                                                        label="Limited"
                                                                        checked={mode === 'limited'}
                                                                        onChange={() => this.handleBucketModeChange(bucketKey, 'limited')}
                                                                        style={{marginRight: '8px'}}
                                                                    />
                                                                    <span style={{
                                                                        visibility: mode === 'limited' ? 'visible' : 'hidden',
                                                                        display: 'inline-flex'
                                                                    }}>
                                                                        <Form.Check
                                                                            inline
                                                                            type="checkbox"
                                                                            id={`perm-list-${bucketKey}`}
                                                                            label="List"
                                                                            checked={permissions.includes('list')}
                                                                            onChange={() => this.handleBucketPermissionChange(bucketKey, 'list')}
                                                                        />
                                                                        <Form.Check
                                                                            inline
                                                                            type="checkbox"
                                                                            id={`perm-read-${bucketKey}`}
                                                                            label="Read"
                                                                            checked={permissions.includes('read')}
                                                                            onChange={() => this.handleBucketPermissionChange(bucketKey, 'read')}
                                                                        />
                                                                        <Form.Check
                                                                            inline
                                                                            type="checkbox"
                                                                            id={`perm-write-${bucketKey}`}
                                                                            label="Write"
                                                                            checked={permissions.includes('write')}
                                                                            onChange={() => this.handleBucketPermissionChange(bucketKey, 'write')}
                                                                        />
                                                                        <Form.Check
                                                                            inline
                                                                            type="checkbox"
                                                                            id={`perm-delete-${bucketKey}`}
                                                                            label="Delete"
                                                                            checked={permissions.includes('delete')}
                                                                            onChange={() => this.handleBucketPermissionChange(bucketKey, 'delete')}
                                                                        />
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}

                                {data.bucketAccessAll === false && buckets.length === 0 && (
                                    <p>No buckets available. Create buckets first to assign permissions.</p>
                                )}
                            </Form>
                        </Container>
                        <div className="d-flex justify-content-end mt-3">
                            <Link
                                className="primary-outline btn btn-md mr3"
                                faIcon={faArrowCircleLeft}
                                href="/access"
                                label="Back to list"
                            />
                            <Button
                                className="btn primary"
                                faIcon={faSave}
                                label="Save"
                                onClick={this.handleSubmit}
                            />
                        </div>
                        <br/>
                        <Alert variant="info">
                            <b>For technical configurations, please consult our documentation</b> <br/>
                            <a href={`${process.env.REACT_APP_HOMEPAGE_URL}/resources/s3-api-permissions-and-compatibility`} target="_blank" rel="noopener">S3 API permissions and compatibility</a>
                        </Alert>
                    </>
                )}
            </Layout>
        );
    }
}

export default RoleSettings
